import { PageConfig } from '../../../models/pages'
import { redirectToCheckout } from './redirect-to-checkout'
import { redirect } from './redirect'
import { setMetadata } from './setMetadata'
import { getMetadata } from './getMetadata'
import { setMetadataItem } from './setMetadataItem'
import { getMetadataItem } from './getMetadataItem'
import { clearMetadata } from './clearMetadata'
import { setCustomQuestionAnswer } from './setCustomQuestionAnswer'
import { getCustomQuestionAnswer } from './getCustomQuestionAnswer'
import { clearCustomQuestionAnswer } from './clearCustomQuestionAnswer'
import { getCustomQuestionAnswers } from './getCustomQuestionAnswers'
import { clearCustomQuestionAnswers } from './clearCustomQuestionAnswers'
import { getProgramDesignations } from './getProgramDesignations'
import { getCampaignProgressMetrics } from './getCampaignProgressMetrics'
import { getUrlParams } from './get-url-params'

export interface ServiceHandlerArgs<TRequest = never, TResponse = never> {
  iframeId: string
  messageId: number
  payload: TRequest
  sendSuccessResponse: (iframeId: string, messageId: number, payload: TResponse) => void
  sendErrorResponse: (iframeId: string, messageId: number, error: string) => void
  pageConfig: PageConfig
}

/**
 * Service handler function signature
 * All service handlers must implement this interface
 */
export type ServiceHandler<TRequest = never, TResponse = never> = (
  args: ServiceHandlerArgs<TRequest, TResponse>,
) => Promise<void> | void

/**
 * Service registry mapping service types to their handlers
 *
 * Note: Uses `any` for generic parameters because TypeScript doesn't support heterogeneous
 * collections of generic types. Each handler has different Request/Response types
 * (e.g., redirect uses RedirectRequest/RedirectResponse, while setMetadata uses
 * SetMetadataRequest/SetMetadataResponse), and there's no way to express "a Record where
 * each value can be a ServiceHandler with its own specific generic parameters" without `any`.
 *
 * Type safety is preserved because:
 * 1. Each handler is individually typed with its specific Request/Response generics
 * 2. The manager imports and uses these specific types via the @custom-embedded-code-sdk alias
 * 3. TypeScript validates the contract at compile-time when handlers are assigned to the registry
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
type ServiceRegistry = Record<string, ServiceHandler<any, any>>

export const SERVICE_HANDLERS: ServiceRegistry = {
  REDIRECT_TO_CHECKOUT: redirectToCheckout,
  REDIRECT: redirect,
  SET_METADATA: setMetadata,
  SET_METADATA_ITEM: setMetadataItem,
  GET_METADATA: getMetadata,
  GET_METADATA_ITEM: getMetadataItem,
  CLEAR_METADATA: clearMetadata,
  SET_CUSTOM_QUESTION_ANSWER: setCustomQuestionAnswer,
  GET_CUSTOM_QUESTION_ANSWER: getCustomQuestionAnswer,
  CLEAR_CUSTOM_QUESTION_ANSWER: clearCustomQuestionAnswer,
  GET_CUSTOM_QUESTION_ANSWERS: getCustomQuestionAnswers,
  CLEAR_CUSTOM_QUESTION_ANSWERS: clearCustomQuestionAnswers,
  GET_PROGRAM_DESIGNATIONS: getProgramDesignations,
  GET_CAMPAIGN_PROGRESS_METRICS: getCampaignProgressMetrics,
  GET_URL_PARAMS: getUrlParams,
} as const
